import type { ReactNode } from "react";

import { cn } from "@/common/utils";
import { useSiteCopy } from "@/landing/lib/site-copy";
import { AwardBadge, EmailJoinForm, TrustLine } from "@/landing/system/actions";
import { Ground, Section, SectionHeading } from "@/landing/system/layout";
import { AgentLogos } from "@/landing/system/marks";

/** Questions and answers as native disclosure rows (no JS), hairline-separated. */
export function Faq({
  items,
  className,
}: {
  items: { question: string; answer: ReactNode }[];
  className?: string;
}) {
  return (
    <div className={cn("border-t border-(--g-rule)", className)}>
      {items.map((item) => (
        <details
          key={item.question}
          className="group border-b border-(--g-rule) [&_summary::-webkit-details-marker]:hidden"
        >
          <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-5 text-[17px] font-medium text-(--g-ink)">
            {item.question}
            <span
              aria-hidden
              className="flex-none text-[22px] leading-none font-normal text-(--g-ink-3) transition-transform group-open:rotate-45"
            >
              +
            </span>
          </summary>
          <div className="max-w-[46rem] pb-6 text-[15px] leading-[1.6] text-(--g-ink-2)">
            {item.answer}
          </div>
        </details>
      ))}
    </div>
  );
}

/**
 * The FAQ section every page uses: heading on the left, questions on the
 * right (stacked on phones). One layout and one default title site-wide.
 */
export function FaqSection({
  items,
  eyebrow = "FAQ",
  title = "Questions, answered.",
  id = "faq",
}: {
  items: { question: string; answer: ReactNode }[];
  eyebrow?: ReactNode;
  title?: ReactNode;
  id?: string;
}) {
  return (
    <Section id={id}>
      <div className="grid gap-10 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] lg:gap-16">
        <SectionHeading eyebrow={eyebrow} title={title} />
        <Faq items={items} />
      </div>
    </Section>
  );
}

/** Closing band before the footer: one line, the email + Join field and the trust line. */
export function JoinBand({
  title,
  sub,
  trust,
  placeholder,
}: {
  title?: ReactNode;
  sub?: ReactNode;
  /** Translated trust line for localized pages (see TrustLine). */
  trust?: ReactNode;
  placeholder?: string;
}) {
  const copy = useSiteCopy();
  return (
    <Ground lattice>
      <Section>
        <SectionHeading
          title={title ?? copy.joinTitle}
          sub={sub ?? copy.joinSub}
        />
        <TrustLine className="mt-[18px]">{trust}</TrustLine>
        <EmailJoinForm className="mt-[34px]" placeholder={placeholder} />
      </Section>
    </Ground>
  );
}

/**
 * Hero for interior pages (features, platforms, models, pricing...): lattice
 * ground, optional eyebrow, the h1, a line under it, the email + Join field and
 * the trust line, with an optional visual on the right.
 */
export function PageHero({
  eyebrow,
  title,
  sub,
  visual,
  showAgents = false,
  showAward = false,
  cta = "form",
  trust,
  placeholder,
  agentsLabel,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  visual?: ReactNode;
  showAgents?: boolean;
  showAward?: boolean;
  cta?: "form" | "none";
  /** Localized pages: translated trust line, email placeholder and "Works from" label. */
  trust?: ReactNode;
  placeholder?: string;
  agentsLabel?: string;
}) {
  const copy = useSiteCopy();
  return (
    <Ground lattice className="-mt-(--header-height)">
      <div className="grid items-center gap-12 px-(--g-margin) pt-[calc(var(--header-height)+clamp(56px,8vw,112px))] pb-[clamp(56px,7vw,104px)] lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
        <div>
          {showAward && <AwardBadge className="mb-[26px]" />}
          <SectionHeading as="h1" eyebrow={eyebrow} title={title} sub={sub} />
          {cta === "form" && (
            <>
              <TrustLine className="mt-[18px]">{trust}</TrustLine>
              <EmailJoinForm className="mt-[34px]" placeholder={placeholder} />
            </>
          )}
          {showAgents && (
            <AgentLogos
              className="mt-[22px]"
              label={agentsLabel ?? copy.worksFrom}
            />
          )}
        </div>
        {visual && <div className="min-w-0">{visual}</div>}
      </div>
    </Ground>
  );
}
